import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { Bell, BellOff, Check } from "lucide-react";

// Convertit la clé VAPID base64url en Uint8Array (requis par pushManager)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function PushSubscribeButton() {
  const [status, setStatus] = useState("idle"); // idle | supported | subscribed | denied | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") { setStatus("denied"); return; }
    // Check existing subscription
    navigator.serviceWorker.getRegistration("/sw-push.js").then((reg) => {
      if (!reg) return setStatus("supported");
      reg.pushManager.getSubscription().then((sub) => {
        setStatus(sub ? "subscribed" : "supported");
      });
    });
  }, []);

  const subscribe = async () => {
    try {
      setMsg("");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setStatus("denied"); setMsg("Permission refusée par le navigateur."); return; }
      const reg = await navigator.serviceWorker.register("/sw-push.js");
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-key");
      const pub = data.public_key || process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!pub) { setMsg("Clé VAPID indisponible."); setStatus("error"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pub),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
      setStatus("subscribed");
      setMsg("✓ Notifications activées. Vous recevrez vos rappels J-3.");
    } catch (e) {
      setStatus("error");
      setMsg("Activation impossible : " + (e.message || "erreur").slice(0, 100));
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw-push.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setStatus("supported");
      setMsg("Notifications désactivées.");
    } catch (e) {
      setMsg("Impossible de désinscrire : " + (e.message || ""));
    }
  };

  if (status === "unsupported") return null;

  return (
    <div className="j-card border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-transparent" data-testid="push-subscribe-card">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${status === "subscribed" ? "bg-emerald-600 text-white" : "bg-slate-800 text-emerald-400"}`}>
          {status === "subscribed" ? <Check className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[#1E293B]">Notifications rappel J-3</div>
          <p className="text-xs text-slate-600 mt-1">Recevez les rappels d'échéance légale <b>directement sur votre téléphone</b>, même sans ouvrir vos emails.</p>
          {msg && <p className="text-xs text-emerald-700 mt-2" data-testid="push-msg">{msg}</p>}
          <div className="mt-3">
            {status === "subscribed" ? (
              <button onClick={unsubscribe} className="btn-ghost text-sm" data-testid="push-unsubscribe-btn"><BellOff className="w-4 h-4" /> Désactiver</button>
            ) : status === "denied" ? (
              <div className="text-xs text-amber-700">Permission bloquée. Autorisez les notifications dans les paramètres du navigateur.</div>
            ) : (
              <button onClick={subscribe} className="btn-emerald text-sm" data-testid="push-subscribe-btn"><Bell className="w-4 h-4" /> Activer les notifications push</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
